import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { Button, Space } from 'antd'
import {
  message,
  notification,
} from 'components/util-components/FeedbackProvider'
import { APP_PREFIX_PATH } from 'configs/AppConfig'
import i18n from 'lang'
import routes from 'routes'
import BillingService from 'services/BillingService'
import store from 'store'
import {
  openErrorNotification,
  openSuccessNotification,
} from '../../utils/openNotification'

export const initialState = {
  cards: null,
  message: '',
  loading: false,
  showMessage: false,
  orders: null,
  wallet: null,
  order: null,
  rewards: { list: [], page: 0 },
  orderIsLoading: false,
  orderError: false,
  ordersIsLoading: false,
  walletIsLoading: false,
  postOrderIsLoading: false,
  addCardIsLoading: false,
  payOrderIsLoading: false,
  editOrderIsLoading: false,
  cancelOrderIsLoading: false,
  rewardsLoading: false,
  refreshRewardsLoading: false,
}

export const getRewards = createAsyncThunk(
  'billing/getRewards',
  async (data, { rejectWithValue }) => {
    try {
      return await BillingService.getRewards()
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Error')
    }
  }
)

export const refreshRewards = createAsyncThunk(
  'billing/refreshRewards',
  async (data, { rejectWithValue }) => {
    try {
      return await BillingService.refreshRewards()
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Error')
    }
  }
)

export const listCards = createAsyncThunk(
  'billing/listCards',
  async (data, { rejectWithValue }) => {
    try {
      const response = await BillingService.listCards(data)

      return response
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Error')
    }
  }
)

export const addCard = createAsyncThunk(
  'billing/postCard',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await BillingService.postCard(data)

      dispatch(
        listCards({
          page: store.getState().billing.cards?.page,
        })
      )

      return response
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Error')
    }
  }
)

export const deleteCard = createAsyncThunk(
  'billing/deleteCard',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await BillingService.deleteCard(data)

      dispatch(
        listCards({
          page: store.getState().billing.cards?.page,
        })
      )

      return response
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Error')
    }
  }
)

export const selectCard = createAsyncThunk(
  'billing/selectCard',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await BillingService.selectCard(data)

      dispatch(listCards({}))

      return response
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Error')
    }
  }
)

export const listOrders = createAsyncThunk(
  'billing/listOrders',
  async (data, { rejectWithValue }) => {
    try {
      const response = await BillingService.listOrders(data)

      return response
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const getOrder = createAsyncThunk(
  'billing/getOrder',
  async (id, { rejectWithValue }) => {
    try {
      return await BillingService.getOrder(id)
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const cancelOrder = createAsyncThunk(
  'billing/cancelOrder',
  async ({ id, updateData = false }, { rejectWithValue, dispatch }) => {
    try {
      const response = await BillingService.cancelOrder(id)

      if (updateData) dispatch(listOrders({}))

      return response
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const listWalletLogs = createAsyncThunk(
  'billing/listWalletLogs',
  async (data, { rejectWithValue }) => {
    try {
      const response = await BillingService.listWalletLogs(data)

      return response
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const getWallet = createAsyncThunk(
  'billing/getWallet',
  async (_, { rejectWithValue }) => {
    try {
      const response = await BillingService.getWallet()

      return response
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const postOrder = createAsyncThunk(
  'billing/postOrder',
  async (data, { rejectWithValue }) => {
    try {
      const response = await BillingService.postOrder(data)

      return response
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const editOrder = createAsyncThunk(
  'billing/editOrder',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await BillingService.editOrder(data)

      if (response) dispatch(getOrder(data.id))

      return response
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const payOrder = createAsyncThunk(
  'billing/payOrder',
  async (data, { rejectWithValue }) => {
    try {
      const response = await BillingService.payOrder(data)

      return response
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Algo deu errado')
    }
  }
)

export const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(refreshRewards.pending, state => {
        state.refreshRewardsLoading = true
      })
      .addCase(refreshRewards.fulfilled, (state, action) => {
        state.refreshRewardsLoading = false
        state.rewards = action.payload
      })
      .addCase(refreshRewards.rejected, state => {
        state.refreshRewardsLoading = false
      })
      .addCase(getRewards.pending, state => {
        state.rewardsLoading = true
      })
      .addCase(getRewards.fulfilled, (state, action) => {
        state.rewardsLoading = false
        state.rewards = action.payload
      })
      .addCase(getRewards.rejected, state => {
        state.rewardsLoading = false
      })
      .addCase(listCards.pending, state => {
        state.loading = true
      })
      .addCase(listCards.fulfilled, (state, action) => {
        state.loading = false
        state.cards = action.payload
      })
      .addCase(listCards.rejected, (state, action) => {
        state.message = action.payload
        state.showMessage = true
        state.loading = false
      })
      .addCase(listOrders.pending, state => {
        state.ordersIsLoading = true
      })
      .addCase(listOrders.fulfilled, (state, action) => {
        state.ordersIsLoading = false
        state.orders = action.payload
      })
      .addCase(listOrders.rejected, state => {
        state.ordersIsLoading = false
      })
      .addCase(getOrder.pending, state => {
        state.orderIsLoading = true
      })
      .addCase(getOrder.fulfilled, (state, action) => {
        state.orderIsLoading = false
        state.order = action.payload
        state.orderError = false
      })
      .addCase(getOrder.rejected, state => {
        state.orderIsLoading = false
        state.orderError = true
      })
      .addCase(cancelOrder.pending, state => {
        state.cancelOrderIsLoading = true
      })
      .addCase(cancelOrder.fulfilled, state => {
        state.cancelOrderIsLoading = false
        openSuccessNotification({
          key: 'cancelOrder',
          message: i18n.t('message.cancel.order.success'),
        })
      })
      .addCase(cancelOrder.rejected, (state, action) => {
        state.cancelOrderIsLoading = false
        openErrorNotification({
          key: 'cancelOrder',
          message: action.payload,
        })
      })
      .addCase(listWalletLogs.pending, state => {
        state.walletLogsIsLoading = true
      })
      .addCase(listWalletLogs.fulfilled, (state, action) => {
        state.walletLogsIsLoading = false
        state.walletLogs = action.payload
      })
      .addCase(listWalletLogs.rejected, state => {
        state.walletLogsIsLoading = false
      })
      .addCase(postOrder.pending, state => {
        state.postOrderIsLoading = true
      })
      .addCase(postOrder.fulfilled, state => {
        state.postOrderIsLoading = false

        openSuccessNotification({
          key: 'postOrder',
          message: i18n.t('message.create.order.success'),
        })
      })
      .addCase(postOrder.rejected, (state, action) => {
        state.postOrderIsLoading = false

        openErrorNotification({
          key: 'postOrder',
          message: action.payload,
        })
      })
      .addCase(getWallet.pending, state => {
        state.walletIsLoading = true
      })
      .addCase(getWallet.fulfilled, (state, action) => {
        state.walletIsLoading = false
        state.wallet = action.payload
      })
      .addCase(getWallet.rejected, state => {
        state.walletIsLoading = false
      })
      .addCase(selectCard.pending, () => {})
      .addCase(selectCard.fulfilled, () => {
        openSuccessNotification({
          key: 'selectCard',
          message: i18n.t('message.select.card.success'),
        })
      })
      .addCase(selectCard.rejected, (_, action) => {
        openErrorNotification({
          key: 'selectCard',
          message: action.payload,
        })
      })
      .addCase(deleteCard.pending, () => {})
      .addCase(deleteCard.fulfilled, () => {
        openSuccessNotification({
          key: 'deleteCard',
          message: i18n.t('message.delete.card.success'),
        })
      })
      .addCase(deleteCard.rejected, (_, action) => {
        openErrorNotification({
          key: 'deleteCard',
          message: action.payload,
        })
      })
      .addCase(addCard.pending, state => {
        state.addCardIsLoading = true
      })
      .addCase(addCard.fulfilled, state => {
        state.addCardIsLoading = false

        openSuccessNotification({
          key: 'addCard',
          message: i18n.t('message.add.card.success'),
        })
      })
      .addCase(addCard.rejected, (state, action) => {
        state.addCardIsLoading = false

        openErrorNotification({
          key: 'addCard',
          message: i18n.t(action.payload),
        })
      })
      .addCase(editOrder.pending, state => {
        state.editOrderIsLoading = true
      })
      .addCase(editOrder.fulfilled, state => {
        openSuccessNotification({
          key: 'editOrder',
          message: i18n.t('message.update.order.success'),
        })
        state.editOrderIsLoading = false
      })
      .addCase(editOrder.rejected, (state, action) => {
        openErrorNotification({
          key: 'editOrder',
          message: action.payload,
        })
        state.editOrderIsLoading = false
      })
      .addCase(payOrder.pending, state => {
        state.payOrderIsLoading = true
      })
      .addCase(payOrder.fulfilled, state => {
        state.payOrderIsLoading = false

        openSuccessNotification({
          key: 'payOrder',
          message: i18n.t('message.pay.order.success'),
        })
      })
      .addCase(payOrder.rejected, (state, action) => {
        state.payOrderIsLoading = false

        const btn = (
          <Space>
            <Button
              type="link"
              size="small"
              onClick={() => notification.destroy('payOrderAction')}
            >
              Outra hora
            </Button>

            <Button
              type="primary"
              size="small"
              onClick={() => {
                notification.destroy('payOrderAction')
                routes.navigate(`${APP_PREFIX_PATH}/settings`)
              }}
            >
              Adicionar agora
            </Button>
          </Space>
        )

        if (action.payload.includes('profile missing')) {
          message.destroy('payOrder')

          notification.open({
            type: 'error',
            key: 'payOrderAction',
            message: i18n.t(action.payload),
            btn,
          })
        } else {
          message.open({
            key: 'payOrder',
            type: 'error',
            content: action.payload,
            duration: 2,
          })
        }
      })
  },
})

export default billingSlice.reducer
